import { api, anonymousApi } from './api';
import { EmployeeRecord, PaginatedApiResponse, FieldFlag, EmployeeStatus, ApiResponse } from '../types';

interface EmployeeFilters {
  status?: EmployeeStatus | EmployeeStatus[];
  searchTerm?: string;
  department?: string;
  position?: string;
  location?: string;
  createdBy?: string;
}

interface EmployeeQueryOptions {
  page?: number;
  limit?: number;
  filters?: EmployeeFilters;
}

export const getEmployees = async (options: EmployeeQueryOptions = {}): Promise<PaginatedApiResponse<EmployeeRecord>> => {
  try {
    const { page = 1, limit = 50, filters = {} } = options;
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });

    // Handle status filter - can be single status or array
    if (filters.status) {
      if (Array.isArray(filters.status)) {
        params.append('status', filters.status.join(','));
      } else {
        params.append('status', filters.status);
      }
    }

    // Add other filters
    if (filters.searchTerm) {
      params.append('searchTerm', filters.searchTerm);
    }
    if (filters.department) {
      params.append('department', filters.department);
    }
    if (filters.position) {
      params.append('position', filters.position);
    }
    if (filters.location) {
      params.append('location', filters.location);
    }
    if (filters.createdBy) {
      params.append('createdBy', filters.createdBy);
    }

    const response = await api.get(`/employees?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch employees:', error);
    throw error;
  }
};

export const getEmployee = async (id: string): Promise<EmployeeRecord> => {
  try {
    const response = await api.get(`/employees/${id}`);
    return response.data.data;
  } catch (error) {
    console.error(`Failed to fetch employee ${id}:`, error);
    throw error;
  }
};

export const createEmployee = async (employeeData: Partial<EmployeeRecord>): Promise<EmployeeRecord> => {
  try {
    console.log('createEmployee: Using anonymousApi for new employee creation');
    const response = await anonymousApi.post('/employees', employeeData);
    return response.data.data;
  } catch (error) {
    console.error('Failed to create employee:', error);
    throw error;
  }
};

export const updateEmployee = async (id: string, employeeData: Partial<EmployeeRecord>): Promise<ApiResponse<EmployeeRecord>> => {
  try {
    const response = await api.put(`/employees/${id}`, employeeData);
    return response.data;
  } catch (error) {
    console.error(`Failed to update employee ${id}:`, error);
    throw error;
  }
};

export const getEmployeeCountByStatus = async (status: string): Promise<number> => {
  try {
    const response = await api.get(`/employees?status=${status}&limit=1`);
    return response.data.pagination?.total || 0;
  } catch (error) {
    console.error(`Failed to fetch count for status ${status}:`, error);
    return 0;
  }
};

export const getDashboardStats = async () => {
  try {
    const response = await api.get('/dashboard/stats');
    return response.data.data; // Extract the data from the success response wrapper
  } catch (error) {
    console.error('Failed to fetch dashboard stats:', error);
    throw error;
  }
};

export const getFieldFlags = async (employeeId: string): Promise<FieldFlag[]> => {
  try {
    const response = await api.get(`/employees/${employeeId}/field-flags`);
    return response.data.data?.flags || [];
  } catch (error) {
    console.error(`Failed to fetch field flags for employee ${employeeId}:`, error);
    throw error;
  }
};

export const createFieldFlags = async (employeeId: string, flags: FieldFlag[]): Promise<void> => {
  try {
    await api.post(`/employees/${employeeId}/field-flags`, { flags });
  } catch (error) {
    console.error(`Failed to create field flags for employee ${employeeId}:`, error);
    throw error;
  }
};

export const saveReviewProgress = async (employeeId: string, checklist: Record<string, boolean>, reviewNotes?: string): Promise<any> => {
  try {
    const response = await api.put(`/employees/${employeeId}/review-progress`, { checklist, reviewNotes });
    return response.data;
  } catch (error) {
    console.error(`Failed to save review progress for employee ${employeeId}:`, error);
    throw error;
  }
};

export const getReviewProgress = async (employeeId: string): Promise<any> => {
  try {
    const response = await api.get(`/employees/${employeeId}/review-progress`);
    return response.data;
  } catch (error) {
    console.error(`Failed to get review progress for employee ${employeeId}:`, error);
    throw error;
  }
};

export const getCandidateSubmission = async (): Promise<any> => {
  try {
    const response = await api.get('/candidate/submission');
    return response.data;
  } catch (error) {
    console.error('Failed to get candidate submission:', error);
    throw error;
  }
};

export const updateCandidateSubmission = async (updates: Partial<EmployeeRecord>): Promise<any> => {
  try {
    const response = await api.put('/candidate/submission', updates);
    return response.data;
  } catch (error) {
    console.error('Failed to update candidate submission:', error);
    throw error;
  }
};

// Draft-specific methods that don't require authentication
export const createEmployeeDraft = async (employeeData: Partial<EmployeeRecord>): Promise<EmployeeRecord> => {
  try {
    const response = await anonymousApi.post('/employees', employeeData);
    return response.data.data;
  } catch (error) {
    console.error('Failed to create employee draft:', error);
    throw error;
  }
};

export const updateEmployeeDraft = async (id: string, employeeData: Partial<EmployeeRecord>): Promise<ApiResponse<EmployeeRecord>> => {
  try {
    console.log('updateEmployeeDraft: Updating draft with ID:', id);
    console.log('updateEmployeeDraft: Using anonymousApi with /register endpoint');
    const response = await anonymousApi.put(`/employees/${id}/register`, employeeData);
    console.log('updateEmployeeDraft: Success response received');
    return response.data;
  } catch (error: any) {
    console.error(`Failed to update employee draft ${id}:`, error);
    console.error('updateEmployeeDraft error response:', error.response?.data);
    console.error('updateEmployeeDraft error status:', error.response?.status);
    console.error('updateEmployeeDraft error headers:', error.response?.headers);
    throw error;
  }
};

export const getEmployeeDraft = async (id: string): Promise<EmployeeRecord> => {
  try {
    console.log('getEmployeeDraft: Fetching draft with /register endpoint for ID:', id);
    const response = await anonymousApi.get(`/employees/${id}/register`);
    return response.data.data;
  } catch (error) {
    console.error(`Failed to fetch employee draft ${id}:`, error);
    throw error;
  }
};

// Smart method that tries to get employee as draft first, then authenticated
export const getEmployeeSmartly = async (id: string): Promise<EmployeeRecord> => {
  try {
    // First try as anonymous draft using the /register endpoint
    console.log('getEmployeeSmartly: Trying /register endpoint first for ID:', id);
    const response = await anonymousApi.get(`/employees/${id}/register`);
    const employee = response.data.data;
    
    console.log('getEmployeeSmartly: Successfully got employee via /register, status:', employee.status);
    return employee;
  } catch (error: any) {
    // If register endpoint fails (403 for non-drafts, 404 for not found), try authenticated request
    console.log('getEmployeeSmartly: /register failed, trying authenticated endpoint. Error:', error.response?.status);
    if (error.response?.status === 403 || error.response?.status === 401 || error.response?.status === 404) {
      return await getEmployee(id);
    }
    throw error;
  }
};

// Draft-specific method that only uses anonymous API (no fallback)
export const getEmployeeDraftOnly = async (id: string): Promise<EmployeeRecord> => {
  try {
    console.log('getEmployeeDraftOnly: Fetching draft with /register endpoint for ID:', id);
    const response = await anonymousApi.get(`/employees/${id}/register`);
    const employee = response.data.data;
    console.log('getEmployeeDraftOnly: Retrieved employee status:', employee.status);
    return employee;
  } catch (error: any) {
    console.error('getEmployeeDraftOnly: Failed to fetch draft:', error);
    throw error;
  }
};

// Smart update method that uses draft API for drafts, authenticated API for others
export const updateEmployeeSmartly = async (id: string, employeeData: Partial<EmployeeRecord>): Promise<ApiResponse<EmployeeRecord>> => {
  try {
    console.log('updateEmployeeSmartly: Checking employee status for ID:', id);
    
    // First check if this is a draft by trying to get it via /register endpoint
    try {
      const currentEmployee = await anonymousApi.get(`/employees/${id}/register`);
      const employee = currentEmployee.data.data;
      
      console.log('updateEmployeeSmartly: Employee accessible via /register, status:', employee.status);
      
      // If we can access via /register, it's definitely a draft, so use draft update
      console.log('updateEmployeeSmartly: Using draft update method via /register endpoint');
      return await updateEmployeeDraft(id, employeeData);
    } catch (registerError: any) {
      // If /register fails, it's likely not a draft or doesn't exist, use authenticated update
      console.log('updateEmployeeSmartly: /register failed, using authenticated update method. Error:', registerError.response?.status);
      return await updateEmployee(id, employeeData);
    }
  } catch (error: any) {
    console.error('updateEmployeeSmartly: Unexpected error:', error);
    throw error;
  }
};

// Export as a service object
export const employeeService = {
  getEmployees,
  getEmployee,
  createEmployee,
  updateEmployee,
  getEmployeeCountByStatus,
  getDashboardStats,
  getFieldFlags,
  createFieldFlags,
  saveReviewProgress,
  getReviewProgress,
  createEmployeeDraft,
  updateEmployeeDraft,
  getEmployeeDraft,
  getEmployeeSmartly,
  updateEmployeeSmartly,
  getEmployeeDraftOnly,
};
